<template>
  <div class="change-password-container">
    <!-- 變更密碼表單 -->
    <a-form layout="vertical" :model="formModel">
      <a-form-item
        class="custom-form-item custom-input password-input"
        :validate-status="fieldErrors.current_password ? 'error' : ''"
        :help="fieldErrors.current_password || ''"
      >
        <label for="current_password" class="form-label">
          目前密碼<span class="required">*</span>
        </label>
        <a-input-password
          id="current_password"
          class="custom-input-style"
          placeholder="請輸入目前的密碼"
          v-model:value="formModel.current_password"
          autocomplete="current-password"
          @input="() => clearFieldError('current_password')"
        />
      </a-form-item>

      <a-form-item
        class="custom-form-item custom-input password-input"
        :validate-status="fieldErrors.password ? 'error' : ''"
        :help="fieldErrors.password || ''"
      >
        <label for="new_password" class="form-label">
          新密碼<span class="required">*</span>
        </label>
        <a-input-password
          id="new_password"
          class="custom-input-style"
          placeholder="請輸入新密碼（至少8碼，含大小寫英數與符號）"
          v-model:value="formModel.password"
          autocomplete="new-password"
          @input="() => clearFieldError('password')"
        />
      </a-form-item>

      <a-form-item
        class="custom-form-item custom-input password-input"
        :validate-status="fieldErrors.password_confirmation ? 'error' : ''"
        :help="fieldErrors.password_confirmation || ''"
      >
        <label for="password_confirmation" class="form-label">
          確認新密碼<span class="required">*</span>
        </label>
        <a-input-password
          id="password_confirmation"
          class="custom-input-style"
          placeholder="請再次輸入新密碼"
          v-model:value="formModel.password_confirmation"
          autocomplete="new-password"
          @input="() => clearFieldError('password_confirmation')"
        />
      </a-form-item>

      <div class="form-actions">
        <a-button class="cancel-btn" @click="handleReset">重設輸入</a-button>
        <a-button
          class="confirm-btn"
          type="primary"
          :loading="submitLoading"
          @click="handleSubmit"
        >
          確認變更密碼
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script setup>
import { reactive, ref } from "vue";
import { message } from "ant-design-vue";
import { authAPI } from "@/services/api";

const submitLoading = ref(false);
const fieldErrors = reactive({});

const formModel = reactive({
  current_password: "",
  password: "",
  password_confirmation: "",
});

const clearFieldError = (field) => {
  if (fieldErrors[field]) {
    delete fieldErrors[field];
  }
};

const handleReset = () => {
  formModel.current_password = "";
  formModel.password = "";
  formModel.password_confirmation = "";
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);
};

const validateClientSide = () => {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

  if (!formModel.current_password) {
    fieldErrors.current_password = "請輸入目前密碼";
  }

  if (!formModel.password || formModel.password.length < 8) {
    fieldErrors.password = "密碼至少需 8 個字元";
  } else {
    const hasUpper = /[A-Z]/.test(formModel.password);
    const hasLower = /[a-z]/.test(formModel.password);
    const hasNum = /[0-9]/.test(formModel.password);
    const hasSpec = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formModel.password);
    if (!hasUpper || !hasLower || !hasNum || !hasSpec) {
      fieldErrors.password = "密碼須包含大小寫英文、數字與特殊符號";
    }
  }

  if (!formModel.password_confirmation) {
    fieldErrors.password_confirmation = "請再次輸入新密碼以確認";
  } else if (formModel.password !== formModel.password_confirmation) {
    fieldErrors.password_confirmation = "兩次輸入的密碼不一致";
  }

  return Object.keys(fieldErrors).length === 0;
};

const handleSubmit = async () => {
  if (!validateClientSide()) {
    message.warning("請確認表單各項密碼規則");
    return;
  }

  try {
    submitLoading.value = true;
    const res = await authAPI.changePassword({
      current_password: formModel.current_password,
      password: formModel.password,
      password_confirmation: formModel.password_confirmation,
    });

    if (res.status >= 200 && res.status < 300) {
      message.success("密碼已成功變更！");
      handleReset();
    }
  } catch (error) {
    const serverMessage = error?.response?.data?.message;
    const errors = error?.response?.data?.content?.errors;

    if (errors && typeof errors === "object") {
      Object.keys(errors).forEach((key) => {
        const messages = errors[key];
        if (Array.isArray(messages) && messages.length > 0) {
          fieldErrors[key] = messages[0];
        }
      });
    }

    if (serverMessage) {
      message.error(serverMessage);
    } else {
      message.error("密碼變更失敗，請檢查輸入內容");
    }
  } finally {
    submitLoading.value = false;
  }
};
</script>

<style scoped>
.change-password-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.custom-form-item {
  margin-bottom: 24px;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: #475569;
  display: inline-block;
  margin-bottom: 6px;
}

.required {
  color: #ef4444;
  margin-left: 4px;
}

.custom-input-style {
  height: 52px;
  border-radius: 12px;
  font-size: 15px;
}

.form-actions {
  display: flex;
  gap: 16px;
  margin-top: 16px;
}

.cancel-btn,
.confirm-btn {
  height: 52px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 500;
  flex: 1;
}

.confirm-btn {
  background-color: var(--color-primary, #022964);
  border-color: var(--color-primary, #022964);
}

@media (max-width: 640px) {
  .form-actions {
    flex-direction: column-reverse;
  }
}
</style>
